import { createStore } from 'react-sweet-state';
import { without } from 'lodash';

import { DEBOUNCED_CALL_STATUS } from 'src/const';
import {
  alignToolForGeneration,
  isDataDrivenGenerationTool,
  isMultiPromptTool,
} from 'src/data/generationTool';
import {
  createTaxonomyScoringMethod,
  DEFAULT_SCORING_METHOD,
  isSameScoringMethod,
} from 'src/data/scoring';
import { addLanguageToGenerationResult } from 'src/data/variation';
import {
  rephraseTextsForDocumentEditor,
  setupMultiPromptRunner,
  suggestDocumentVariations,
} from 'src/graphql/generation';
import { saveProjectEditorContentDebounced } from 'src/graphql/saveProjectEditorContent';
import { parseErrors } from 'src/lib/graphql';
import { captureMessage } from 'src/lib/sentry';

import { getGenerationLoaderContent } from '../../utils';

import { shortDebouncedCallback } from './utils';

const NAME = 'view-project-document';

const INITIAL_STATE = {
  scoringMethod: DEFAULT_SCORING_METHOD,
  loadingScoringMethod: false,
  secondaryScoringMethod: null,

  editorContent: '',
  generating: false,
  variationsInjectionQueue: null,
  injectionQueueFirstVariationId: null,
  editorSelectedVariationInstanceId: null,
  editorHoveredVariationInstanceId: null,
  detailsPanelVisibleForVariationInstanceId: null,
  detailsPanelVisibleForVariationVersion: null,
  revertingVariationIds: [],
  rescoringVariationIds: [],

  generatingLoaderText: null,

  hasHiddenSuggestedPersonas: false,

  addedPopoverTitle: false,
  savedCount: 0,

  skipLockVariationIds: [],
};

const PRIVATE_ACTIONS = {
  initEditor:
    () =>
    ({ setState }, { project }) => {
      if (!project) {
        return;
      }

      setState({
        editorContent: project.editorContent || '',
      });
    },

  pushToVariationsInjector:
    (variations, blockedReason) =>
    ({ setState }) => {
      const firstVariation = variations?.[0];
      if (!firstVariation) {
        return;
      }

      const updatedVariations = variations.map((variation) => {
        if (firstVariation.classifiedGenerationTool && !blockedReason) {
          return { ...variation, generationTool: firstVariation.classifiedGenerationTool };
        }
        return { ...variation };
      });

      setState({
        variationsInjectionQueue: {
          dataDriven: isDataDrivenGenerationTool(
            firstVariation.classifiedGenerationTool || firstVariation.generationTool
          ),
          variations: updatedVariations,
        },
      });
    },
};

/**
 * Public available actions
 */
export const ACTIONS = {
  init:
    () =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.initEditor());
    },

  update: () => () => {},

  getProject:
    () =>
    ({ getState }) => {
      return getState().project;
    },

  getScoringMethod:
    () =>
    ({ getState }) => {
      return getState().scoringMethod;
    },
  setScoringMethod:
    (newValue) =>
    ({ getState, setState }) => {
      // Do not override current value if it is the same - in case we have some effect dependent on it
      const { scoringMethod } = getState();
      if (isSameScoringMethod(scoringMethod, newValue)) {
        return;
      }

      setState({ scoringMethod: newValue });
    },
  setSecondaryScoringMethod:
    (value) =>
    ({ setState }) => {
      setState({ secondaryScoringMethod: value });
    },
  getSecondaryScoringMethod:
    () =>
    ({ getState }) => {
      return getState().secondaryScoringMethod;
    },
  setTaxonomyId:
    (id) =>
    ({ setState }) => {
      const scoringMethod = createTaxonomyScoringMethod(id);
      setState({
        scoringMethod,
      });

      return scoringMethod;
    },
  getLoadingScoringMethod:
    () =>
    ({ getState }) => {
      return getState().loadingScoringMethod;
    },
  setLoadingScoringMethod:
    (value) =>
    ({ setState }) => {
      setState({ loadingScoringMethod: value });
    },

  setInjectionQueueFirstVariationId:
    (injectionQueueFirstVariationId) =>
    ({ setState }) => {
      setState({ injectionQueueFirstVariationId });
    },

  emptyVariationsInjectionQueue:
    () =>
    ({ setState }) => {
      setState({ variationsInjectionQueue: null });
    },

  getEditorContent:
    () =>
    ({ getState }) => {
      return getState().editorContent;
    },

  setEditorContent:
    (content) =>
    ({ setState, getState }, { project }) => {
      setState({
        editorContent: content,
      });

      const callback = (status, result) => {
        if (status === DEBOUNCED_CALL_STATUS.error) {
          captureMessage('Error while auto-saving whiteboard content', {
            level: 'warning',
            extra: {
              error: parseErrors(result).join('\n'),
              projectId: project.id,
              content,
            },
          });
        }
      };

      saveProjectEditorContentDebounced(project.id, content, callback);
    },

  setEditorSelectedVariation:
    (instanceId) =>
    ({ setState }) => {
      setState({ editorSelectedVariationInstanceId: instanceId });
    },
  setEditorHoveredVariation:
    (instanceId) =>
    ({ setState }) => {
      shortDebouncedCallback(instanceId, (resultId) => {
        setState({ editorHoveredVariationInstanceId: resultId });
      });
    },

  suggestVariations:
    (params, options) =>
    async ({ getState, setState, dispatch }, { project, getDebugAcPrompt }) => {
      // Add more generation params
      const generationParams = {
        ...params,
        projectId: project.id,
        debugAcPrompt: getDebugAcPrompt(),
      };

      // Generate and store text that will be shown on the floating "loader" panel
      const loaderContent = getGenerationLoaderContent(generationParams);
      dispatch(ACTIONS.setGeneratingLoaderText(loaderContent));

      // We align tool after "loader text" was generated, cause all "freestyle" tools
      // should be sent to BE always with the same `freestyle` tool
      generationParams.generationTool = alignToolForGeneration(generationParams.generationTool);

      setState({ generating: true });

      try {
        // Wait for brief extraction if needed
        let briefExtraction;
        if (options?.briefExtractionPromise) {
          briefExtraction = await options?.briefExtractionPromise;
          if (briefExtraction?.blockingErrors) {
            return { briefExtractionResult: briefExtraction };
          }

          const extractedBriefs = briefExtraction?.extractedBriefs;
          if (extractedBriefs && Object.keys(extractedBriefs).length > 0) {
            // This is legacy support, to avoid risk the url is used somewhere else in the system (tracking events, for e.g.)
            generationParams.url = Object.keys(extractedBriefs)[0];
            generationParams.urlExtractions = Object.entries(extractedBriefs).map(
              ([url, extractedBrief]) => ({
                url,
                extractedBrief,
              })
            );
          }
        }

        if (isMultiPromptTool(params.generationTool)) {
          return await setupMultiPromptRunner(generationParams);
        }

        // Run generation itself
        const result = await suggestDocumentVariations(generationParams);
        if (briefExtraction?.errors) {
          result.briefExtractionResult = briefExtraction;
        }
        return addLanguageToGenerationResult(result);
      } finally {
        setState({ generating: false });
        dispatch(ACTIONS.setGeneratingLoaderText(null));
      }
    },

  rephraseTexts:
    (params) =>
    async ({ setState, getState, dispatch }, { project, getDebugAcPrompt }) => {
      const generationParams = {
        ...params,
        projectId: project.id,
        debugAcPrompt: getDebugAcPrompt(),
      };

      // Generate and store text that will be shown on the floating "loader" panel
      const loaderContent = getGenerationLoaderContent(generationParams);
      dispatch(ACTIONS.setGeneratingLoaderText(loaderContent));

      // Run generation itself
      setState({ generating: true });

      try {
        const result = await rephraseTextsForDocumentEditor(generationParams);
        return addLanguageToGenerationResult(result);
      } finally {
        setState({ generating: false });
        dispatch(ACTIONS.setGeneratingLoaderText(null));
      }
    },

  appendVariation:
    (variation, blockedReason) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.pushToVariationsInjector([variation], blockedReason));
    },

  appendVariations:
    (variations) =>
    ({ dispatch }) => {
      dispatch(PRIVATE_ACTIONS.pushToVariationsInjector(variations));
    },

  openVariationDetailsPanel:
    (instanceId, version) =>
    ({ setState }) => {
      setState({
        detailsPanelVisibleForVariationInstanceId: instanceId,
        detailsPanelVisibleForVariationVersion: version,
      });
    },
  closeVariationDetailsPanel:
    () =>
    ({ setState }) => {
      setState({
        detailsPanelVisibleForVariationInstanceId: null,
        detailsPanelVisibleForVariationVersion: null,
      });
    },
  resetVariationDetailsPanelVersion:
    () =>
    ({ setState }) => {
      setState({ detailsPanelVisibleForVariationVersion: null });
    },
  writeVariationDetailsPanelVersionIfEmpty:
    (version) =>
    ({ getState, setState }) => {
      const { detailsPanelVisibleForVariationVersion: current } = getState();
      if (current == null) {
        setState({ detailsPanelVisibleForVariationVersion: version });
      }
    },

  setGeneratingLoaderText:
    (generatingLoaderText) =>
    ({ setState }) => {
      setState({ generatingLoaderText });
    },

  setHasHiddenSuggestedPersonas:
    (hasHiddenSuggestedPersonas) =>
    ({ setState }) => {
      setState({ hasHiddenSuggestedPersonas });
    },

  markRevertingVariationId:
    (id, reverting) =>
    ({ getState, setState }) => {
      const { revertingVariationIds } = getState();
      const newList = reverting
        ? [...revertingVariationIds, id]
        : revertingVariationIds.filter((n) => n !== id);
      setState({ revertingVariationIds: newList });
    },

  markRescoringVariationId:
    (id, reverting) =>
    ({ getState, setState }) => {
      const { rescoringVariationIds } = getState();
      const newList = reverting
        ? [...rescoringVariationIds, id]
        : rescoringVariationIds.filter((n) => n !== id);
      setState({ rescoringVariationIds: newList });
    },
  isRescoringVariationId:
    (id) =>
    ({ getState }) => {
      const { rescoringVariationIds } = getState();
      return rescoringVariationIds.includes(id);
    },

  setAddedPopoverTitle:
    (value) =>
    ({ setState }) => {
      setState({ addedPopoverTitle: value });
    },

  incrementSavedCount:
    (value) =>
    ({ setState, getState }) => {
      setState({ savedCount: getState().savedCount + value });
    },

  initSavedCount:
    () =>
    ({ setState }) => {
      setState({ savedCount: 0 });
    },

  markSkipLockForVariation:
    (variationId, skipLock) =>
    ({ getState, setState }) => {
      const { skipLockVariationIds } = getState();
      const newList = skipLock
        ? [...skipLockVariationIds, variationId]
        : without(skipLockVariationIds, variationId);

      setState({ skipLockVariationIds: newList });
    },

  getSkipLockVariationIds:
    () =>
    ({ getState }) => {
      return getState().skipLockVariationIds;
    },
};

export const DocumentViewStore = createStore({
  initialState: INITIAL_STATE,
  actions: ACTIONS,
  name: NAME,
});
